import { useCallback, useContext, useEffect, useState } from 'react';
import { ServiceContext } from '../../context/ServiceContext';

/**
 * Fetches user information when UserInfoUpdate event was triggered
 */

export interface PersonalData {
  _version?: string;
  readonly salutation?: string;
  readonly academicTitle?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly nickname?: string;
}

export type UserInfo = {
  personalData?: PersonalData;
};

export function useUserUpdatedData(): UserInfo {
  const [personalData, setPersonalData] = useState<PersonalData>();
  const { authService, eventService } = useContext(ServiceContext);
  const getPersonalData = useCallback(async () => {
    try {
      const response = await authService.fetch('v3/customers/__userId__/personalData', {
        resourceHostId: 'customer-profile',
      });

      const version = response.headers.get('etag');
      if (!response.ok || !version) {
        throw Error(`Error fetching personal data: ${response.statusText} (${response.status})`);
      }

      const data = (await response.json()) as PersonalData;
      setPersonalData({ _version: version, ...data });
    } catch (error) {
      console.error(error);
    }
  }, [authService]);

  useEffect(() => {
    void getPersonalData();
    setPersonalData(personalData);
  }, [getPersonalData]);

  useEffect(() => {
    if (!eventService) return undefined;

    const subscription = eventService.subscribeToUserInfoUpdate(() => {
      void getPersonalData();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [eventService, getPersonalData]);

  return { personalData };
}
